// src/context/FreemiumContext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import http from 'helpers/requests';
import { isEmbedPath } from 'modules/Embed/useEmbedConfig';

const FreemiumContext = createContext();

export const useFreemium = () => {
  return useContext(FreemiumContext);
};

// Partner iframe embeds have no Clerk user, so calling /restrictions would
// either 401 or be misinterpreted as a different partner. Short-circuit to
// unlimited so freemium banners and archive popovers stay out of the embed.
const isEmbedContext = () => typeof window !== 'undefined' && isEmbedPath(window.location.pathname);

export const FreemiumProvider = ({ children }) => {
  const [restrictions, setRestrictions] = useState([]); //this state triggers all freemium features
  const isLimitExceeded = (restrictions) =>
    restrictions.filter((restriction) => restriction?.exceeded).length > 0;

  // This could be set dynamically based on your app logic
  const triggerLimitExceeded = useCallback(async () => {
    // A verifier firm has no plan and no restrictions (verifier-firm-foundation VF18); the
    // endpoint would only 403 it on every load.
    if (isEmbedContext() || window.Clerk?.organization?.publicMetadata?.type === 'verifier') {
      setRestrictions([]);
      return;
    }
    return http
      .get('/restrictions')
      .then((res) => {
        const data = res?.data;
        setRestrictions(data);
      })
      .catch((err) => {
        setRestrictions([]);
      });
  }, []);

  useEffect(() => {
    triggerLimitExceeded();
  }, [triggerLimitExceeded]);

  const data = useMemo(
    () => ({
      hasExceededLimit: isLimitExceeded(restrictions),
      restrictions,
      triggerLimitExceeded,
    }),
    [restrictions, triggerLimitExceeded],
  );

  return <FreemiumContext.Provider value={data}>{children}</FreemiumContext.Provider>;
};
